import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { updateFaviconBadge, storeOriginalFavicon, restoreOriginalFavicon } from '../utils/faviconBadge';

interface Notification {
  notification_id: number;
  message: string;
  notification_type: string;
  sent_via: string;
  is_read: boolean;
  sent_at: string;
  user_id: number;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  fetchNotifications: () => Promise<void>;
  markAsRead: (notificationIds: number[]) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

type NotificationProviderProps = {
  children: ReactNode;
  /**
   * When false, disables the background polling interval (useful for tests).
   * Defaults to true.
   */
  polling?: boolean;
  /**
   * When false, skips the initial fetch-on-mount even if a user exists (useful for tests).
   * Defaults to true.
   */
  autoFetch?: boolean;
};

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children, polling = true, autoFetch = true }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get('/api/notifications');
      setNotifications(res.data || []);
    } catch (err: any) {
      if (err.response?.status === 401) {
        // Don't set error for auth issues, just clear notifications
        setNotifications([]);
        return;
      }
      setError(err.response?.data?.error || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const markAsRead = useCallback(async (notificationIds: number[]) => {
    try {
      await Promise.all(
        notificationIds.map(id => 
          axios.put(`/api/notifications/${id}`, { is_read: true })
        )
      );
      // Refresh notifications to update the UI
      await fetchNotifications();
    } catch (err: any) {
      if (err.response?.status === 401) {
        return;
      }
      setError('Failed to mark notifications as read');
    }
  }, [fetchNotifications]);

  // Calculate unread count
  const unreadCount = notifications.filter(n => !n.is_read).length;

  // Store original favicon on mount
  useEffect(() => {
    storeOriginalFavicon();
  }, []);

  // Update favicon badge when unread count changes
  useEffect(() => {
    updateFaviconBadge(unreadCount);
  }, [unreadCount]);

  // Cleanup: restore original favicon when component unmounts
  useEffect(() => {
    return () => {
      restoreOriginalFavicon();
    };
  }, []);

  // Fetch notifications when user changes
  useEffect(() => {
    if (user) {
      if (autoFetch) {
        fetchNotifications();
      }
    } else {
      setTimeout(() => setNotifications([]), 0);
    }
  }, [user, fetchNotifications, autoFetch]);

  // Set up periodic refresh of notifications (every 30 seconds) only when enabled and user is authenticated
  useEffect(() => {
    if (!user || !polling) return;
    const intervalId = setInterval(() => {
      fetchNotifications();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [user, polling, fetchNotifications]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    loading,
    error
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
